/**
 * **영토 특산**(Todo9). 땅마다 그 땅의 특산(말·철갑·철 무기·투구·방패·군기)이 있다. 받는 길은 둘:
 *
 * - **지키면 쌓인다**: 수비대를 둔 땅이 차례마다 특산 점수를 쌓고, `SPECIALTY_GOAL`(100)을 넘으면 그 특산 하나. 추첨이 아니라
 *   누적이다 — 운이 아니라 지킨 시간과 투입이 보상이 된다(사용자가 고른 권장안).
 * - **빼앗으면 하나**: 땅을 빼앗은 판에 그 땅의 특산 하나를 곧바로 준다 — 공격할 까닭.
 *
 * 멀티플레이(서버)에서도 같은 규칙이 되도록 짰다. 서버에서는 한 땅을 여러 사람이 지키므로 점수에 **제 몫**(내 수비대 전투력 ÷
 * 그 땅 플레이어 수비대 전투력 합)을 곱한다 — 1인용은 늘 100%다(`share`). 함정 막기:
 * - **최소 투입**(`MIN_STAKE`): 병사 한 명씩 여러 땅에 흩어 특산을 긁어모으지 못하게.
 * - **한 차례를 넘긴 수비대만**(`Garrison.since`): 차례 직전에 넣었다 거두는 것을 막는다.
 * - **전선 가산, 후방 감산**: 위험(부상·전사)을 지는 땅이 더 받는다 — 위험 없는 후방에 쌓아 두는 담합을 막는다.
 * - **땅 수 체감**: 수입처럼 n곳이면 100/(100+15·(n−1)).
 * - **점수는 사람의 장부**(`RealmState.ledger`): 땅을 잃어도 쌓은 점수는 남는다(더 쌓이지 않을 뿐).
 *
 * 특산은 상점에 없는 3티어 이상이고, 그 계정의 나라가 입을 수 있는 것만 준다(남의 나라 전용은 빼고). 수도에 가까운 땅
 * (수비 깊이 13 이상)은 4티어까지 준다.
 */
import { Rng } from '../sim/rng.ts';
import { EQUIPMENT, Slot, Source } from '../sim/equipment.ts';
import type { EquipDef } from '../sim/equipment.ts';
import type { Culture } from '../sim/troops.ts';
import type { Army } from './army.ts';
import { LEDGER_MAX, STAKE_MIN, TERRITORIES, TERRITORY_BY_ID, garrisonDepth, neighborsOf } from './realm.ts';
import type { RealmState, TerritoryDef } from './realm.ts';
import { addToStash, canWear } from './stash.ts';

export const SpecialtyKind = {
  Horse: 'horse',
  Armor: 'armor',
  Weapon: 'weapon',
  Helm: 'helm',
  Shield: 'shield',
  Banner: 'banner',
} as const;
export type SpecialtyKind = (typeof SpecialtyKind)[keyof typeof SpecialtyKind];

export const SPECIALTY_NAMES: Record<SpecialtyKind, string> = {
  horse: '말',
  armor: '갑옷',
  weapon: '철 무기',
  helm: '투구·관',
  shield: '방패',
  banner: '군기',
};

const KIND_SLOT: Record<SpecialtyKind, Slot> = {
  horse: Slot.Mount,
  armor: Slot.Armor,
  weapon: Slot.Weapon,
  helm: Slot.Helm,
  shield: Slot.Shield,
  banner: Slot.Banner,
};

/** 특산 하나에 드는 점수. */
export const SPECIALTY_GOAL = 100;
/** 한 차례의 기본 점수. 전선 ×1.5, 후방 ×0.5. */
export const SPECIALTY_BASE = 10;
/** 점수를 쌓으려면 둔 병사가 이만큼 있어야 한다. */
export const MIN_STAKE = STAKE_MIN;
/** 이 수비 깊이부터 4티어까지 준다(수도 곁). */
const TIER4_DEPTH = 13;

/**
 * 그 땅의 특산이 주는 물건: 그 칸의 3티어 이상·깊이 전리품(상점·시즌 전용 밖)·이 나라가 입을 수 있는 것. 깊은 땅이면 4티어까지.
 * 처음에는 티어만 보았더니 시즌 전용 장비(`Source.Season`, 4티어)가 생기자 점령·누적 특산으로 금 등급 없이 나왔다(리뷰).
 */
export function specialtyItems(t: TerritoryDef, culture: Culture): EquipDef[] {
  const top = garrisonDepth(t.id) >= TIER4_DEPTH ? 4 : 3;
  return EQUIPMENT.filter(
    (e) => e.slot === KIND_SLOT[t.specialty] && e.source === Source.Depth && e.tier >= 3 && e.tier <= top && canWear(culture, e),
  );
}

/** 특산 하나를 고른다(시드 난수 — 같은 상태면 같은 물건). 고를 것이 없으면 null. */
function pickItem(t: TerritoryDef, culture: Culture, seed: number): EquipDef | null {
  const list = specialtyItems(t, culture);
  if (list.length === 0) return null;
  return list[new Rng(seed >>> 0).int(list.length)];
}

/** 그 땅을 칠 수 있는 남의 땅과 맞닿았나(전선). */
function frontline(realm: RealmState, id: string): boolean {
  const me = realm.owner[id];
  return neighborsOf(id).some((n) => realm.owner[n] !== me);
}

/**
 * 이 차례에 그 땅이 쌓는 특산 점수(체감 전, 몫 100%). 수비대가 없거나, 최소 투입에 못 미치거나, 한 차례를 넘기지 않았으면 0.
 * 차례 안(`realm.turn`은 이미 오른 값)에서 부른다.
 */
export function specialtyRate(realm: RealmState, id: string): number {
  const g = realm.garrisons?.[id];
  if (!g || g.soldiers.length < MIN_STAKE) return 0;
  // 최소 투입을 채운 차례 뒤 한 차례가 온전히 지나야 센다(차례 직전에 넣었다 거두는 것을 막는다). 채운 차례를 모르면 안 센다.
  if (g.since === undefined || realm.turn - g.since < 2) return 0;
  return frontline(realm, id) ? Math.floor((SPECIALTY_BASE * 3) / 2) : Math.floor(SPECIALTY_BASE / 2);
}

/** 화면이 적을 "한 차례에 +N점"(다음 차례 기준, 체감 포함). */
export function specialtyPreview(realm: RealmState, id: string): number {
  const next = { ...realm, turn: realm.turn + 1 };
  const places = TERRITORIES.filter((t) => specialtyRate(next, t.id) > 0).length;
  const rate = specialtyRate(next, id);
  return places === 0 ? 0 : Math.floor((rate * 100) / (100 + 15 * (places - 1)));
}

/**
 * 차례의 특산 단계: 쌓고, 넘으면 물건을 준다. 받은 것을 돌려준다(소식에 적는다). 땅 순서로 돈다.
 * 서버에서는 여기서 플레이어마다 제 몫(`share`)을 곱한다 — 1인용은 1.
 */
export function specialtyPhase(army: Army, realm: RealmState): { territory: string; item: string }[] {
  const share = 1;
  // 최소 투입 아래로 내려간 수비대(부상·대장 교체로 넘친 병사)는 기다림을 처음부터 — 다시 채운 차례부터 센다.
  for (const t of TERRITORIES) {
    const g = realm.garrisons?.[t.id];
    if (g && g.soldiers.length < MIN_STAKE) delete g.since;
    // 채운 차례를 모르는 수비대(특산이 생기기 전의 저장)는 이 차례부터 센다 — 두지 않으면 영영 쌓이지 않았다.
    else if (g && g.since === undefined) g.since = realm.turn;
  }
  const rates = TERRITORIES.map((t) => ({ t, rate: specialtyRate(realm, t.id) })).filter((x) => x.rate > 0);
  if (rates.length === 0) return [];
  const scale = 100 + 15 * (rates.length - 1);
  const got: { territory: string; item: string }[] = [];
  const ledger = (realm.ledger ??= {});
  for (const { t, rate } of rates) {
    ledger[t.id] = (ledger[t.id] ?? 0) + Math.floor((rate * 100 * share) / scale);
    // 한 차례에 한 땅은 하나까지 — 한 차례의 점수(15점 남짓)로는 둘이 될 수 없다. 깨진 장부가 끝없는 지급이 되지 않게도 막는다.
    if (ledger[t.id] >= SPECIALTY_GOAL) {
      const item = pickItem(t, army.culture, Math.imul(realm.turn + 1, 0x9e3779b1) ^ Math.imul(TERRITORIES.indexOf(t) + 1, 0x85ebca6b) ^ ledger[t.id]);
      ledger[t.id] = Math.min(ledger[t.id] - SPECIALTY_GOAL, LEDGER_MAX);
      if (item) {
        addToStash(army, item.id);
        got.push({ territory: t.id, item: item.id });
      }
    }
  }
  return got;
}

/** 땅을 빼앗은 판의 특산 하나(곧바로 창고로). 줄 것이 없으면 null. */
export function captureGift(army: Army, realm: RealmState, id: string): string | null {
  const t = TERRITORY_BY_ID.get(id);
  if (!t) return null;
  const item = pickItem(t, army.culture, Math.imul(realm.turn + 7, 0x9e3779b1) ^ Math.imul(TERRITORIES.indexOf(t) + 1, 0xc2b2ae35));
  if (!item) return null;
  addToStash(army, item.id);
  return item.id;
}
